//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { InvoiceFormTypes } from "models/formTypes";
import { DefaultInvoiceDeliverySettings } from "models/formTypes/invoice";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion

function useDefaultDeliverySettings() {
  const defaultInvoiceSendOption = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultInvoiceSendOption
  );
  const defaultInvoiceMailSubject = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultInvoiceMailSubject
  );
  const defaultInvoiceMailMessage = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultInvoiceMailMessage
  );
  const defaultReminderCost = useSelector((store: AppState) => store.auth.selectedCompany.defaultReminderCost);
  const defaultIsAutomaticRemindersEnabled = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultIsAutomaticRemindersEnabled
  );
  const defaultAutomaticReminderPeriod = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultAutomaticReminderPeriod
  );
  const defaultAutomaticReminderDelay = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultAutomaticReminderDelay
  );
  const defaultAutomaticReminderAmountLimit = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultAutomaticReminderAmountLimit
  );
  const defaultAutomaticReminderCountLimit = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultAutomaticReminderCountLimit
  );
  const defaultInvoiceReminderNotes = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultInvoiceReminderNotes
  );
  const defaultInvoiceReminderSendOption = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultInvoiceReminderSendOption
  );
  const defaultReminderMailSubject = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultReminderMailSubject
  );
  const defaultReminderMailMessage = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultReminderMailMessage
  );
  const defaultReminderFileNameTemplate = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultReminderFileNameTemplate
  );

  const defaultInvoiceNotes = useSelector((store: AppState) => store.auth.selectedCompany.defaultInvoiceNotes);

  const getDefaultDeliverySettings = (customer?: WintSuperkollAPIModel.Customer): DefaultInvoiceDeliverySettings => {
    const settings: DefaultInvoiceDeliverySettings = {
      invoiceSendType: customer?.invoiceDeliverySettings?.defaultInvoiceSendOption ?? defaultInvoiceSendOption,
      automaticReminderSendType:
        customer?.invoiceReminderSettings?.invoiceReminderSendOption ?? defaultInvoiceReminderSendOption,
      invoiceTitleTemplate: customer?.invoiceDeliverySettings?.invoiceTitleTemplate ?? defaultInvoiceMailSubject,
      invoiceMessageTemplate: customer?.invoiceDeliverySettings?.invoiceMessageTemplate ?? defaultInvoiceMailMessage,
      reminderCost: customer?.invoiceReminderSettings?.reminderCost ?? defaultReminderCost,
      isAutomaticRemindersEnabled:
        customer?.invoiceReminderSettings?.isAutomaticRemindersEnabled ?? defaultIsAutomaticRemindersEnabled,
      automaticReminderPeriod:
        customer?.invoiceReminderSettings?.automaticReminderPeriod ?? defaultAutomaticReminderPeriod,
      automaticReminderDelay:
        customer?.invoiceReminderSettings?.automaticReminderDelay ?? defaultAutomaticReminderDelay,
      automaticReminderAmountLimit:
        customer?.invoiceReminderSettings?.automaticReminderAmountLimit ?? defaultAutomaticReminderAmountLimit,
      automaticReminderCountLimit:
        customer?.invoiceReminderSettings?.automaticReminderCountLimit ?? defaultAutomaticReminderCountLimit,
      invoiceReminderNotesTemplate:
        customer?.invoiceReminderSettings?.invoiceReminderNotesTemplate ?? defaultInvoiceReminderNotes,
      invoiceReminderTitleTemplate:
        customer?.invoiceReminderSettings?.invoiceReminderTitleTemplate ?? defaultReminderMailSubject,
      invoiceReminderMessageTemplate:
        customer?.invoiceReminderSettings?.invoiceReminderMessageTemplate ?? defaultReminderMailMessage,
      invoiceReminderFileNameTemplate:
        customer?.invoiceReminderSettings?.invoiceReminderFilenameTemplate ?? defaultReminderFileNameTemplate,
      invoiceNotesTemplate: customer?.invoiceDeliverySettings?.invoiceNotes ?? defaultInvoiceNotes,
    };

    return settings;
  };

  const getUpdatedDefaultDeliverySettings = (
    customer: WintSuperkollAPIModel.Customer,
    create: InvoiceFormTypes.Create
  ): { otherSettings: InvoiceFormTypes.OtherSettings; recurringSettings: InvoiceFormTypes.RecurringSettings } => {
    const defaultSettings = getDefaultDeliverySettings(customer);
    const otherSettings: InvoiceFormTypes.OtherSettings = {
      ...create.otherSettings,
      reminderCost: defaultSettings.reminderCost,
      isAutomaticRemindersEnabled: defaultSettings.isAutomaticRemindersEnabled,
      automaticReminderAmountLimit: defaultSettings.automaticReminderAmountLimit,
      automaticReminderDelay: defaultSettings.automaticReminderDelay,
      automaticReminderPeriod: defaultSettings.automaticReminderPeriod,
      automaticReminderCountLimit: defaultSettings.automaticReminderCountLimit,
      automaticReminderSendType: defaultSettings.automaticReminderSendType,
      automaticReminderNotes: defaultSettings.invoiceReminderNotesTemplate,
      automaticReminderSendMail: {
        ...create.otherSettings.automaticReminderSendMail,
        emailSubject: defaultSettings.invoiceReminderTitleTemplate,
        emailMessage: defaultSettings.invoiceReminderMessageTemplate,
      },
      notes: defaultSettings.invoiceNotesTemplate,
    };

    const recurringSettings: InvoiceFormTypes.RecurringSettings = {
      ...create.recurringSettings,
      recurringSendMail: {
        ...create.recurringSettings.recurringSendMail,
        emailSubject: defaultSettings.invoiceTitleTemplate,
        emailMessage: defaultSettings.invoiceMessageTemplate,
      },
    };

    return { otherSettings, recurringSettings };
  };

  return { getDefaultDeliverySettings, getUpdatedDefaultDeliverySettings };
}

export default useDefaultDeliverySettings;
